import {ButtonProps} from "@chakra-ui/react";

/**
 * Shared geometry for every modal footer control, so a secondary and a primary action never sit
 * at different heights or widths as their labels change length. `minW` also keeps the row from
 * reflowing when a label is swapped ("Next" -> "Create", "Add languages" -> "Add 2 languages").
 */
export const modalFooterButtonProps: ButtonProps = {
  type: "button",
  size: "md",
  minW: "7rem",
  borderRadius: "lg",
  fontWeight: "semibold",
  // Specific properties only — `transition: all` would animate layout on every re-render.
  transition: "transform 120ms cubic-bezier(0.2, 0, 0, 1), background-color 200ms linear, border-color 200ms linear",
  _active: {transform: "scale(0.96)"},
};

/** Secondary: reads as an escape hatch, never competing with the primary action. */
export const modalSecondaryButtonProps: ButtonProps = {
  ...modalFooterButtonProps,
  variant: "outline",
  borderColor: "n.500",
  color: "n.100",
  _hover: {bg: "n.500", borderColor: "n.400"},
};

/**
 * Primary: the single brandPurple call to action on the screen. The nested `_disabled` inside
 * `_hover` is load-bearing — without it the hover background wins on a disabled button and a
 * blocked action still looks clickable.
 */
export const modalPrimaryButtonProps: ButtonProps = {
  ...modalFooterButtonProps,
  colorScheme: "brandPurple",
  // `bg` is set explicitly because Chakra's dark-mode solid variant resolves a colorScheme to its
  // .200 shade — white on that is 2.01:1 and reads as disabled. .500/.600 give 5.54:1 and 7.47:1.
  bg: "brandPurple.500",
  color: "white",
  _disabled: {bg: "n.500", color: "n.400", opacity: 1, cursor: "not-allowed", transform: "none"},
  _hover: {bg: "brandPurple.600", _disabled: {bg: "n.500"}},
};
